import React from "react";
import { Box } from "@mui/material";
import { CardHeader, CardShell, JiraTile, MONO, OutlineButton, PrimaryButton, StatusChip, T } from "./chrome";
import { jiraConnectActions } from "./flowStore";
import { useJiraConnectFlow } from "./useJiraConnectFlow";

export default function AuthorizeCard(): React.ReactElement {
    const flow = useJiraConnectFlow();
    const errored = flow.error?.stage === "authorizing" && flow.stage === "authorizing";
    const done = flow.account !== null && flow.stage !== "authorizing";

    if (done) {
        return (
            <CardShell testId="jc-authorize">
                <CardHeader tool="integration_oauth_authorize" right={<StatusChip tone="success" label="Authorized" />} />
                <Box sx={{ padding: "13px 14px", display: "flex", alignItems: "center", gap: "12px" }}>
                    <JiraTile />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontSize: "13.5px", fontWeight: 600, color: T.fg1 }}>Atlassian · Jira</Box>
                        <Box sx={{ fontSize: "12px", color: T.fg4 }}>
                            Signed in as {flow.account?.email ?? flow.account?.domain}
                        </Box>
                    </Box>
                </Box>
            </CardShell>
        );
    }

    return (
        <CardShell testId="jc-authorize">
            <CardHeader
                tool="integration_oauth_authorize"
                right={<StatusChip tone={errored ? "error" : "neutral"} label={errored ? "Authorization failed" : "Waiting for authorization"} />}
            />
            <Box sx={{ padding: "13px 14px" }}>
                {errored ? (
                    <>
                        <Box sx={{ fontSize: "12px", color: T.fg3, marginBottom: "10px" }}>{flow.error?.message}</Box>
                        <OutlineButton label="Retry" onClick={jiraConnectActions.retry} />
                    </>
                ) : !flow.savedChecked ? (
                    <Box sx={{ fontSize: "12px", color: T.fg4 }}>Checking saved connections…</Box>
                ) : flow.pickerMode === "saved" && flow.savedConnection ? (
                    <>
                        <Box sx={{ fontSize: "12px", color: T.fg4, marginBottom: "9px" }}>
                            1 Jira connection on file. Choose it or add another.
                        </Box>
                        <Box sx={{
                            display: "flex", alignItems: "center", gap: "10px", border: `1px solid ${T.borderStrong}`,
                            borderRadius: "9px", background: T.bgInput, padding: "9px 12px", marginBottom: "9px"
                        }}>
                            <JiraTile size={22} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box component="span" sx={{ fontSize: "12.5px", fontWeight: 600, color: T.fg1 }}>
                                    {flow.savedConnection.email ?? flow.savedConnection.domain}
                                </Box>{" "}
                                <Box component="span" sx={{ fontSize: "11px", color: T.fg4, fontFamily: MONO }}>
                                    {flow.savedConnection.domain}
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            <PrimaryButton label="Use this account" onClick={jiraConnectActions.useSavedAccount} />
                            <OutlineButton label="Connect another account" onClick={jiraConnectActions.connectAnother} />
                        </Box>
                    </>
                ) : (
                    <>
                        <Box
                            component="input"
                            placeholder="your-site.atlassian.net"
                            value={flow.domainInput}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => jiraConnectActions.setDomainInput(e.target.value)}
                            sx={{
                                display: "block", width: "100%", boxSizing: "border-box",
                                border: `1px solid ${T.borderStrong}`, borderRadius: "9px", background: T.bgInput,
                                padding: "9px 12px", fontSize: "12.5px", color: T.fg1, fontFamily: "inherit",
                                marginBottom: "9px", outline: "none",
                                "&:focus": { borderColor: T.primary }
                            }}
                        />
                        <PrimaryButton label="Authorize in popup" onClick={jiraConnectActions.startOAuth} />
                    </>
                )}
            </Box>
        </CardShell>
    );
}
